import { 
  Box, 
  Button, 
  useTheme, 
  useMediaQuery, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Typography, 
  CircularProgress,
  IconButton
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { tokens } from "../../theme";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Header from "../../components/Header";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import { customBaseUrl } from "../../action/https";

const ManageTerminals = () => {
  const [details, setDetails] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({
    terminalName: "",
    terminalCategory: "",
    amount: "",
    quantity: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);

  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();
  const { success, snackError } = useAuth();

  const getTerminals = async () => {
    try {
      const response = await axiosPrivate.get(`${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/type/all`);
      const resData = response?.data;
      let types = resData?.data?.terminalTypes || resData?.terminalTypes || resData?.respBody || resData?.data;
      if (types && !Array.isArray(types) && Array.isArray(types.terminalTypes)) {
        types = types.terminalTypes;
      }
      setDetails(Array.isArray(types) ? types.filter(t => !t.deleted) : []);
    } catch (error) {
      console.log(error);
      setDetails([]);
    }
  };

  useEffect(() => {
    getTerminals();
  }, []);

  const handleCreateOpen = () => {
    setFormData({
      terminalName: "",
      terminalCategory: "",
      amount: "",
      quantity: "",
    });
    setCreateOpen(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    try {
      const response = await axiosPrivate.post(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/type/create`,
        {
          amount: String(formData.amount),
          quantity: parseInt(formData.quantity, 10) || 0,
          terminalCategory: formData.terminalCategory,
          description: "High-performance POS terminal",
          terminalName: formData.terminalName,
          isActive: true,
        }
      );
      if (response?.data?.success || response?.data?.respCode === 0) {
        success("Terminal type created successfully");
        setCreateOpen(false);
        getTerminals();
      } else {
        snackError(response?.data?.message || "Failed to create terminal type");
      }
    } catch (error) {
      snackError(error?.response?.data?.message || error?.message || error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleEditOpen = (row) => {
    setSelectedRow(row);
    setFormData({
      terminalName: row.terminalName || "",
      terminalCategory: row.terminalCategory || "",
      amount: row.amount || "",
      quantity: row.quantity || 0,
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setBtnLoading(true);
    try {
      const response = await axiosPrivate.put(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/type/${selectedRow.id}`,
        {
          terminalName: formData.terminalName,
          terminalCategory: formData.terminalCategory,
          quantity: parseInt(formData.quantity, 10) || 0,
          amount: String(formData.amount),
          deleted: false,
        }
      );
      if (response?.data?.success || response?.data?.respCode === 0) {
        success("Terminal type updated successfully");
        setEditOpen(false);
        getTerminals();
      } else {
        snackError(response?.data?.message || "Failed to update terminal type");
      }
    } catch (error) {
      snackError(error?.response?.data?.message || error?.message || error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleDeleteOpen = (row) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const handleDeleteSubmit = async () => {
    setBtnLoading(true);
    try {
      const response = await axiosPrivate.delete(
        `${customBaseUrl.posUrl}/pos-terminal/api/v1/terminal/type/${selectedRow.id}`
      );
      if (response?.data?.success || response?.data?.respCode === 0) {
        success("Terminal type deleted successfully");
        setDeleteOpen(false);
        getTerminals();
      } else {
        snackError(response?.data?.message || "Failed to delete terminal type");
      }
    } catch (error) {
      snackError(error?.response?.data?.message || error?.message || error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const columns = [
    { field: "terminalName", headerName: "Type Name", width: 200 },
    {
      field: "terminalCategory",
      headerName: "Terminal Category",
      width: 200,
    },
    {
      field: "amount",
      headerName: "Terminal Cost",
      width: 150,
      valueGetter: (params) => params.row.amount ? `NGN ${params.row.amount}` : "N/A"
    },
    {
      field: "quantity",
      headerName: "Quantity",
      cellClassName: "name-column--cell",
      width: 120,
    },
    {
      field: "actions",
      headerName: "Action",
      width: 150,
      renderCell: (params) => {
        return (
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={() => handleEditOpen(params.row)}
              sx={{ color: colors.orangeAccent[500] }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteOpen(params.row)}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Grid xs={12} sm={12} md={6} lg={3.6} xl={3}>
          <Box
            display={smScreen ? "flex" : "block"}
            flexDirection={smScreen ? "row" : "column"}
            justifyContent={smScreen ? "space-between" : "start"}
            alignItems={smScreen ? "center" : "start"}
          >
            <Header title="TERMINALS" subtitle="Manage Terminals Types" />
          </Box>
        </Grid>
        <Grid xs={12} sm={12} md={6} lg={3.6} xl={3}>
          <Box
            display="flex"
            justifyContent="end"
            paddingRight={1.2}
            paddingTop={1}
          >
            <Button
              underline="none"
              onClick={handleCreateOpen}
              sx={{
                backgroundColor: colors.orangeAccent[700],
                color: colors.grey[100],
                fontSize: "14px",
                fontWeight: "bold",
                padding: "10px 20px",
                marginLeft: "15px",
                "&:hover": {
                  backgroundColor: colors.orangeAccent[800],
                }
              }}
            >
              <AddIcon sx={{ mr: "10px" }} />
              Create Terminal Type
            </Button>
          </Box>
        </Grid>
        <Box
          m="10px"
          width="100%"
          height="65vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .name-column--cell": {
              color: colors.orangeAccent[300],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.redAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.redAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.orangeAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            getRowId={(row) => {
              return row.id || row.terminalName || row.terminalCategory;
            }}
            rows={details}
            columns={columns}
          />
        </Box>
      </Grid>

      {/* CREATE MODAL */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleCreateSubmit}>
          <DialogTitle sx={{ fontWeight: "bold", fontSize: "20px" }}>Create New Terminal Type</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "20px", pt: 1 }}>
            <TextField
              required
              fullWidth
              label="Terminal Type Name (e.g. PAX-2)"
              name="terminalName"
              value={formData.terminalName}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              label="Terminal Category (e.g. PAX)"
              name="terminalCategory"
              value={formData.terminalCategory}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              type="number"
              label="Terminal Cost (NGN)"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              type="number"
              label="Quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setCreateOpen(false)} disabled={btnLoading}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={btnLoading}
              sx={{ backgroundColor: colors.orangeAccent[700], color: colors.grey[100] }}
            >
              {btnLoading ? <CircularProgress size={24} /> : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* EDIT MODAL */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleEditSubmit}>
          <DialogTitle sx={{ fontWeight: "bold", fontSize: "20px" }}>Update Terminal Type</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "20px", pt: 1 }}>
            <TextField
              required
              fullWidth
              label="Terminal Type Name"
              name="terminalName"
              value={formData.terminalName}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              label="Terminal Category"
              name="terminalCategory"
              value={formData.terminalCategory}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              type="number"
              label="Terminal Cost (NGN)"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
            />
            <TextField
              required
              fullWidth
              type="number"
              label="Quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setEditOpen(false)} disabled={btnLoading}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={btnLoading}
              sx={{ backgroundColor: colors.orangeAccent[700], color: colors.grey[100] }}
            >
              {btnLoading ? <CircularProgress size={24} /> : "Update"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle sx={{ fontWeight: "bold" }}>Delete Terminal Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete terminal type <strong>{selectedRow?.terminalName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={btnLoading}>Cancel</Button>
          <Button onClick={handleDeleteSubmit} color="error" variant="contained" disabled={btnLoading}>
            {btnLoading ? <CircularProgress size={24} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageTerminals;
